/** Form XML overlay pageId in PanelsBlock / registry (`FormOverlayPage`). */
export const FORM_OVERLAY_PAGE_ID_PREFIX = 'overlay-'

export type OverlayRegistrySyncOptions = {
  originOverlayPageId?: string
  forceFieldNames?: string[]
}

export type OverlayFieldPageRef = {
  fieldName?: unknown
  pageId?: unknown
  isDialog?: boolean
  refill?: (data?: unknown) => void
}

export type OverlayPageFieldSyncExtra = {
  fieldName?: string
  registeredElements?: Iterable<OverlayFieldPageRef>
  pendingOverlayRefillFields?: Iterable<string>
  /** Overlay field names duplicated in form XML (survives lazy-dialog unmount). */
  layoutDuplicatedFieldNames?: Iterable<string>
}

export function isFormOverlayPageId(pageId: unknown): boolean {
  return String(pageId ?? '').startsWith(FORM_OVERLAY_PAGE_ID_PREFIX)
}

export function toFormOverlayPageId(overlayXmlId: unknown): string | undefined {
  const raw = String(overlayXmlId ?? '').trim()
  if (!raw) return undefined
  if (isFormOverlayPageId(raw)) return raw
  return `${FORM_OVERLAY_PAGE_ID_PREFIX}${raw}`
}

export function resolveOriginOverlayPageId(pageId: unknown): string | undefined {
  const id = String(pageId ?? '').trim()
  return isFormOverlayPageId(id) ? id : undefined
}

/**
 * Page-level overlay actions pass XML id (`38698`); widgets use `overlay-38698`.
 * Numeric origin becomes overlay only if that overlay page is already registered.
 */
export function normalizeOriginOverlayPageId(
  originPageId: unknown,
  registeredElementPageIds?: Iterable<unknown>,
): string | undefined {
  const prefixed = resolveOriginOverlayPageId(originPageId)
  if (prefixed) return prefixed
  const raw = String(originPageId ?? '').trim()
  if (!raw || isFormOverlayPageId(raw)) return undefined
  const candidate = toFormOverlayPageId(raw)
  if (!candidate || !registeredElementPageIds) return undefined
  for (const id of registeredElementPageIds) {
    if (String(id ?? '') === candidate) return candidate
  }
  return undefined
}

export function overlayXmlIdFromPageId(pageId: unknown): string | undefined {
  const raw = String(pageId ?? '').trim()
  if (!isFormOverlayPageId(raw)) return undefined
  const id = raw.slice(FORM_OVERLAY_PAGE_ID_PREFIX.length).trim()
  return id || undefined
}

export function overlayFieldPageIds(
  elements: Iterable<OverlayFieldPageRef>,
  fieldName: string,
): string[] {
  const name = String(fieldName ?? '').trim()
  if (!name) return []
  const pages = new Set<string>()
  for (const el of elements) {
    if (String(el.fieldName ?? '').trim() !== name) continue
    const pageId = String(el.pageId ?? '').trim()
    if (isFormOverlayPageId(pageId)) pages.add(pageId)
  }
  return [...pages]
}

export function overlayFieldNameIsDuplicated(
  elements: Iterable<OverlayFieldPageRef>,
  fieldName: string,
): boolean {
  return overlayFieldPageIds(elements, fieldName).length > 1
}

export function overlayFieldNameIsDuplicatedInLayout(
  fieldName: string,
  layoutDuplicatedFieldNames?: Iterable<string>,
): boolean {
  const name = String(fieldName ?? '').trim()
  if (!name || !layoutDuplicatedFieldNames) return false
  for (const pending of layoutDuplicatedFieldNames) {
    if (String(pending ?? '').trim() === name) return true
  }
  return false
}

function iterableHasFieldName(names: Iterable<string> | undefined, fieldName: string): boolean {
  return overlayFieldNameIsDuplicatedInLayout(fieldName, names)
}

/** Unscoped/main must not copy a field that already lives on another overlay page, or a pending overlay refill. */
export function shouldSkipUnscopedOverlayFieldCopy(
  elementPageId: unknown,
  fieldName: string,
  registeredElements: Iterable<OverlayFieldPageRef>,
  pendingFieldNames?: Iterable<string>,
  layoutDuplicatedFieldNames?: Iterable<string>,
): boolean {
  if (!isFormOverlayPageId(elementPageId)) return false
  const name = String(fieldName ?? '').trim()
  if (!name) return false
  if (iterableHasFieldName(pendingFieldNames, name)) return true
  if (overlayFieldNameIsDuplicatedInLayout(name, layoutDuplicatedFieldNames)) return true
  return overlayFieldNameIsDuplicated(registeredElements, name)
}

export function collectShownOverlayPageIds(
  visibilities: Array<{ level?: unknown; id?: unknown; visible?: unknown }>,
  knownOverlayXmlIds?: Iterable<string | number>,
): string[] {
  const known = new Set<string>()
  for (const id of knownOverlayXmlIds ?? []) {
    const raw = String(id ?? '').trim()
    if (!raw) continue
    known.add(raw)
    const prefixed = toFormOverlayPageId(raw)
    if (prefixed) known.add(prefixed)
  }
  const out: string[] = []
  for (const entry of visibilities) {
    if (!entry?.visible) continue
    const level = String(entry.level ?? '').toUpperCase()
    if (level !== 'PAGE' && level !== 'OVERLAYPAGE') continue
    const rawId = String(entry.id ?? '').trim()
    if (!rawId) continue
    const prefixed = toFormOverlayPageId(rawId)
    if (!prefixed) continue
    if (known.size > 0) {
      const xmlId = overlayXmlIdFromPageId(prefixed) ?? rawId
      if (!known.has(rawId) && !known.has(prefixed) && !known.has(xmlId)) continue
    }
    out.push(prefixed)
  }
  return out
}

export function resolveOverlayRefillTargets<T extends OverlayFieldPageRef>(
  candidates: T[],
  shownOverlayPageIds: Iterable<string>,
): T[] {
  const overlayCandidates = candidates.filter((c) => isFormOverlayPageId(c.pageId))
  if (overlayCandidates.length === 0) return candidates

  const shown = new Set<string>()
  for (const id of shownOverlayPageIds) {
    const prefixed = toFormOverlayPageId(id) ?? String(id ?? '').trim()
    if (prefixed) shown.add(prefixed)
  }
  if (shown.size > 0) {
    return overlayCandidates.filter((c) => shown.has(String(c.pageId ?? '').trim()))
  }

  const pages = new Set(overlayCandidates.map((c) => String(c.pageId ?? '').trim()))
  if (pages.size <= 1) return overlayCandidates

  const permanents = overlayCandidates.filter((c) => c.isDialog !== true)
  return permanents.length > 0 ? permanents : overlayCandidates
}

export function shouldDeferOverlayElementRefill(
  elementPageId: unknown,
  fieldName: string,
  registeredElements: Iterable<OverlayFieldPageRef>,
  layoutDuplicatedFieldNames?: Iterable<string>,
): boolean {
  if (isFormOverlayPageId(elementPageId)) return true
  if (overlayFieldNameIsDuplicatedInLayout(fieldName, layoutDuplicatedFieldNames)) return true
  return overlayFieldNameIsDuplicated(registeredElements, fieldName)
}

/** Overlay TEXT/scalar: do not fall through to shared pageFields for a layout-duplicated name. */
export function shouldSkipOverlaySharedPageFields(
  elementPageId: unknown,
  fieldName: string,
  layoutDuplicatedFieldNames?: Iterable<string>,
): boolean {
  if (!isFormOverlayPageId(elementPageId)) return false
  return overlayFieldNameIsDuplicatedInLayout(fieldName, layoutDuplicatedFieldNames)
}

/**
 * UniData → widget sync: skip when origin and target are two different overlay pages.
 * Unscoped origin still fills unique overlay fields; duplicate overlay names (live registry
 * or form XML layout) and pending overlay refill are skipped.
 */
export function shouldSyncPageFieldToElementPage(
  elementPageId: unknown,
  originPageId: unknown,
  extra?: OverlayPageFieldSyncExtra,
): boolean {
  const element = String(elementPageId ?? '').trim()
  const origin = String(originPageId ?? '').trim()
  if (isFormOverlayPageId(origin) && isFormOverlayPageId(element)) {
    return element === origin
  }
  if (
    extra?.fieldName &&
    shouldSkipUnscopedOverlayFieldCopy(
      element,
      extra.fieldName,
      extra.registeredElements ?? [],
      extra.pendingOverlayRefillFields,
      extra.layoutDuplicatedFieldNames,
    )
  ) {
    return false
  }
  return true
}

export function overlayRegistrySyncOptions(
  originPageId: unknown,
  extra?: { forceFieldNames?: string[] },
): OverlayRegistrySyncOptions {
  const out: OverlayRegistrySyncOptions = {}
  const originOverlayPageId = resolveOriginOverlayPageId(originPageId)
  if (originOverlayPageId) out.originOverlayPageId = originOverlayPageId
  else if (String(originPageId ?? '').trim()) {
    out.originOverlayPageId = String(originPageId).trim()
  }
  if (extra?.forceFieldNames?.length) out.forceFieldNames = extra.forceFieldNames
  return out
}

export function findRegisteredElementPageId(
  elements: Iterable<{ panelId?: unknown; elementId?: unknown; pageId?: unknown }>,
  panelId: unknown,
  elementId: unknown,
): string {
  const pid = String(panelId ?? '')
  const eid = String(elementId ?? '')
  if (!pid || !eid) return ''
  for (const el of elements) {
    if (String(el.panelId ?? '') === pid && String(el.elementId ?? '') === eid) {
      return String(el.pageId ?? '')
    }
  }
  return ''
}

/** Overlay widget: registry slot is the display source when the key already exists. */
export function overlayWidgetUsesRegistrySlot(
  elementPageId: unknown,
  registryEntry: unknown,
): boolean {
  return isFormOverlayPageId(elementPageId) && registryEntry !== undefined
}

/** TABLE/TREETABLE on an overlay page: do not follow shared pageFields while the widget slot exists. */
export function resolveOverlayTableDisplayValue(
  elementPageId: unknown,
  registryEntry: unknown,
  pageEntry: unknown,
): unknown {
  if (overlayWidgetUsesRegistrySlot(elementPageId, registryEntry)) {
    if (
      registryEntry &&
      typeof registryEntry === 'object' &&
      !Array.isArray(registryEntry) &&
      'value' in registryEntry
    ) {
      return (registryEntry as { value?: unknown }).value
    }
    return registryEntry
  }
  return pageEntry
}
